/**
 * Seed themes + the curated editable-token vocabulary.
 *
 * 2026-06-01 — Chris ask: "all of the UI colors and fonts to be defined by a
 * single file in a single location so themes are super easy to edit." This is
 * that single location for the EDITABLE surface — the curated set of tokens
 * the Themes settings tab exposes as color pickers, and the seed values for
 * each built-in theme (so users can clone one as a starting point).
 *
 * The full token vocabulary stays in `frontend/src/styles/tokens.css` — that
 * file is the runtime CSS source of truth. This module mirrors the SUBSET of
 * tokens that:
 *   (a) the Themes tab exposes for per-color editing, and
 *   (b) drive the rest of the token graph via the universal :root color-mix
 *       block in tokens.css (so a 15-token override is enough to repaint the
 *       whole UI).
 *
 * Remaster U5 (2026-07-04): added `accent-fg` (text on a solid accent fill)
 * and `editor-fg` (rich-text prose ink) to the editable set — the audit found
 * both are theme-tuned in tokens.css but weren't cloneable/editable. The
 * backend stores tokens as an opaque {name: color} map, so no contract change.
 *
 * Derived tokens (--accent-soft, --accent-line, --bg-app, --primary-soft,
 * etc.) compute via color-mix and CSS var fallbacks; they do NOT belong here.
 */

/** The curated editable set — order matters; this is also the picker layout. */
export const EDITABLE_TOKENS: readonly EditableToken[] = [
  // Surface ramp (canvas → most-elevated)
  { key: 'bg-0', label: 'Page canvas', group: 'Surfaces' },
  { key: 'bg-1', label: 'Panel', group: 'Surfaces' },
  { key: 'bg-2', label: 'Card / hover', group: 'Surfaces' },
  { key: 'bg-3', label: 'Active / pressed', group: 'Surfaces' },
  { key: 'bg-4', label: 'Floating menu', group: 'Surfaces' },
  // Foreground (text)
  { key: 'fg-0', label: 'Primary text', group: 'Text' },
  { key: 'fg-2', label: 'Secondary text', group: 'Text' },
  { key: 'fg-3', label: 'Muted text', group: 'Text' },
  { key: 'editor-fg', label: 'Editor prose', group: 'Text' },
  // Lines + accent + status
  { key: 'line', label: 'Border', group: 'Structure' },
  { key: 'accent', label: 'Accent (action)', group: 'Accents' },
  { key: 'accent-fg', label: 'Text on accent', group: 'Accents' },
  { key: 'primary', label: 'Primary (data)', group: 'Accents' },
  { key: 'good', label: 'Good / success', group: 'Status' },
  { key: 'danger', label: 'Danger / error', group: 'Status' },
] as const;

export type EditableTokenKey =
  | 'bg-0' | 'bg-1' | 'bg-2' | 'bg-3' | 'bg-4'
  | 'fg-0' | 'fg-2' | 'fg-3' | 'editor-fg'
  | 'line'
  | 'accent' | 'accent-fg' | 'primary'
  | 'good' | 'danger';

export interface EditableToken {
  /** CSS variable name without the leading `--` (e.g. `bg-0`). */
  key: EditableTokenKey;
  /** Human-readable label in the editor UI. */
  label: string;
  /** Section heading in the editor UI (Surfaces / Text / Structure / Accents / Status). */
  group: 'Surfaces' | 'Text' | 'Structure' | 'Accents' | 'Status';
}

export interface SeedTheme {
  id: string;
  label: string;
  /** A short colour description shown in the picker. */
  hint: string;
  /** Whether this theme is in the light-color register (drives default fg ramp). */
  isLight: boolean;
  /** The curated-token starting values. Exact OKLCH strings copied verbatim
   *  from `frontend/src/styles/tokens.css`'s `:root[data-theme='<id>']` block. */
  tokens: Record<EditableTokenKey, string>;
}

/**
 * The seven published themes (U5: seeds and the user-visible catalog are the
 * same set). When a new one is added to `tokens.css`, mirror the 15 curated
 * tokens here so users can clone it.
 *
 * Keep these in sync with tokens.css's per-theme `:root[data-theme='…']`
 * blocks (CI doesn't enforce this — the cost of drift is a wrong starting
 * point for new clones, not a runtime bug).
 */
export const SEED_THEMES: readonly SeedTheme[] = [
  {
    id: 'ember',
    label: 'Ember',
    hint: 'Warm near-black with ember-orange accent',
    isLight: false,
    tokens: {
      'bg-0': 'oklch(0.165 0.010 64)',
      'bg-1': 'oklch(0.193 0.012 64)',
      'bg-2': 'oklch(0.235 0.014 62)',
      'bg-3': 'oklch(0.275 0.016 62)',
      'bg-4': 'oklch(0.315 0.018 62)',
      'fg-0': 'oklch(0.962 0.005 75)',
      'fg-2': 'oklch(0.668 0.009 68)',
      'fg-3': 'oklch(0.512 0.009 66)',
      'editor-fg': 'oklch(0.920 0.012 70)',
      'line': 'oklch(0.295 0.014 62)',
      'accent': 'oklch(0.745 0.155 54)',
      'accent-fg': 'oklch(0.18 0.030 54)',
      'primary': 'oklch(0.74 0.10 230)',
      'good': 'oklch(0.78 0.13 152)',
      'danger': 'oklch(0.68 0.17 26)',
    },
  },
  {
    id: 'blue-slate',
    label: 'Blue Slate',
    hint: 'Cool neutral — azure accent, elevation via shadow',
    isLight: false,
    tokens: {
      'bg-0': 'oklch(0.190 0.018 258)',
      'bg-1': 'oklch(0.220 0.020 258)',
      'bg-2': 'oklch(0.265 0.022 256)',
      'bg-3': 'oklch(0.305 0.022 256)',
      'bg-4': 'oklch(0.345 0.022 256)',
      'fg-0': 'oklch(0.972 0.004 258)',
      'fg-2': 'oklch(0.668 0.011 256)',
      'fg-3': 'oklch(0.534 0.013 256)',
      'editor-fg': 'oklch(0.920 0.010 248)',
      'line': 'oklch(0.350 0.018 258 / 0.60)',
      'accent': 'oklch(0.715 0.128 244)',
      'accent-fg': 'oklch(0.16 0.020 244)',
      'primary': 'oklch(0.715 0.128 244)',
      'good': 'oklch(0.78 0.13 158)',
      'danger': 'oklch(0.68 0.16 18)',
    },
  },
  {
    id: 'parchment',
    label: 'Parchment',
    hint: 'Light, paper-warm — ink red-orange accent',
    isLight: true,
    tokens: {
      'bg-0': 'oklch(0.948 0.009 78)',
      'bg-1': 'oklch(0.974 0.006 78)',
      'bg-2': 'oklch(0.995 0.003 78)',
      'bg-3': 'oklch(0.918 0.011 76)',
      'bg-4': 'oklch(0.880 0.013 74)',
      'fg-0': 'oklch(0.225 0.012 62)',
      'fg-2': 'oklch(0.488 0.013 60)',
      'fg-3': 'oklch(0.628 0.011 60)',
      'editor-fg': 'oklch(0.245 0.018 50)',
      'line': 'oklch(0.862 0.012 72)',
      'accent': 'oklch(0.552 0.188 33)',
      'accent-fg': 'oklch(0.985 0.010 75)',
      'primary': 'oklch(0.50 0.12 250)',
      'good': 'oklch(0.55 0.14 150)',
      'danger': 'oklch(0.52 0.20 26)',
    },
  },
  {
    id: 'slate',
    label: 'Slate',
    hint: 'Slate + nixie orange + liquid-oxygen blue',
    isLight: false,
    tokens: {
      'bg-0': 'oklch(0.178 0.004 260)',
      'bg-1': 'oklch(0.200 0.005 260)',
      'bg-2': 'oklch(0.225 0.006 260)',
      'bg-3': 'oklch(0.255 0.006 260)',
      'bg-4': 'oklch(0.295 0.007 260)',
      'fg-0': 'oklch(0.965 0.006 250)',
      'fg-2': 'oklch(0.78 0.010 250)',
      'fg-3': 'oklch(0.62 0.012 250)',
      // slate has no per-block overrides for these two — values below are the
      // resolved universal :root default (accent-fg) and slate's --fg (editor-fg).
      'editor-fg': 'oklch(0.965 0.006 250)',
      'line': 'oklch(0.30 0.005 260)',
      'accent': 'oklch(0.75 0.135 55)',
      'accent-fg': 'oklch(0.18 0.03 54)',
      'primary': 'oklch(0.86 0.055 215)',
      'good': 'oklch(0.74 0.12 148)',
      'danger': 'oklch(0.68 0.15 25)',
    },
  },
  {
    id: 'terminal-yellow',
    label: 'Terminal Yellow',
    hint: 'Amber-phosphor CRT — monochrome amber on dark glass',
    isLight: false,
    tokens: {
      'bg-0': 'oklch(0.150 0.014 70)',
      'bg-1': 'oklch(0.180 0.018 70)',
      'bg-2': 'oklch(0.222 0.022 72)',
      'bg-3': 'oklch(0.262 0.024 72)',
      'bg-4': 'oklch(0.302 0.026 74)',
      'fg-0': 'oklch(0.865 0.150 82)',
      'fg-2': 'oklch(0.660 0.140 78)',
      'fg-3': 'oklch(0.540 0.120 76)',
      'editor-fg': 'oklch(0.840 0.150 82)',
      'line': 'oklch(0.330 0.040 74)',
      'accent': 'oklch(0.800 0.170 68)',
      'accent-fg': 'oklch(0.160 0.030 68)',
      'primary': 'oklch(0.840 0.140 94)',
      'good': 'oklch(0.800 0.140 125)',
      'danger': 'oklch(0.640 0.185 33)',
    },
  },
  {
    id: 'terminal-green',
    label: 'Terminal Green',
    hint: 'Green-phosphor CRT — classic green terminal',
    isLight: false,
    tokens: {
      'bg-0': 'oklch(0.150 0.014 150)',
      'bg-1': 'oklch(0.180 0.018 150)',
      'bg-2': 'oklch(0.222 0.022 150)',
      'bg-3': 'oklch(0.262 0.024 150)',
      'bg-4': 'oklch(0.302 0.026 150)',
      'fg-0': 'oklch(0.870 0.170 148)',
      'fg-2': 'oklch(0.665 0.155 145)',
      'fg-3': 'oklch(0.545 0.130 144)',
      'editor-fg': 'oklch(0.845 0.165 148)',
      'line': 'oklch(0.330 0.045 146)',
      'accent': 'oklch(0.820 0.185 145)',
      'accent-fg': 'oklch(0.150 0.030 145)',
      'primary': 'oklch(0.850 0.155 128)',
      'good': 'oklch(0.820 0.165 150)',
      'danger': 'oklch(0.640 0.185 33)',
    },
  },
  {
    id: 'terminal-nixie',
    label: 'Terminal Nixie',
    hint: 'Nixie-tube reddish-orange CRT',
    isLight: false,
    tokens: {
      'bg-0': 'oklch(0.150 0.016 38)',
      'bg-1': 'oklch(0.180 0.020 38)',
      'bg-2': 'oklch(0.222 0.024 36)',
      'bg-3': 'oklch(0.262 0.026 36)',
      'bg-4': 'oklch(0.302 0.028 36)',
      'fg-0': 'oklch(0.820 0.185 42)',
      'fg-2': 'oklch(0.640 0.175 38)',
      'fg-3': 'oklch(0.530 0.150 37)',
      'editor-fg': 'oklch(0.800 0.180 42)',
      'line': 'oklch(0.330 0.055 38)',
      'accent': 'oklch(0.760 0.205 40)',
      'accent-fg': 'oklch(0.150 0.030 40)',
      'primary': 'oklch(0.825 0.150 62)',
      'good': 'oklch(0.800 0.150 148)',
      'danger': 'oklch(0.605 0.205 22)',
    },
  },
];

/** Look up a seed by id. */
export function getSeedTheme(id: string): SeedTheme | undefined {
  return SEED_THEMES.find((s) => s.id === id);
}
